import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  alpha,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import CustomButton from './CustomButton';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Apps as AppsIcon,
  Groups as GroupsIcon,
  People as PeopleIcon,
  SupportAgent as SupportAgentIcon,
  HelpOutline as HelpOutlineIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  CastConnected as CastConnectedIcon,
  Devices as DevicesIcon,
  Shield as ShieldIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../state/AuthContext';
import { useThemeMode } from '../state/ThemeContext';
import { useTranslation } from 'react-i18next';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import NotificationBadge from './NotificationBadge';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;
const HEADER_HEIGHT = 64;

const NAV_ITEMS: Array<{
  labelKey: string;
  path: string;
  icon: JSX.Element;
  roles?: Array<'master_full' | 'dealer_admin' | 'tenant_admin' | 'tenant_user'>;
}> = [
  {
    labelKey: 'nav.dashboard',
    path: '/metrics',
    icon: <DashboardIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.sessions',
    path: '/sessions',
    icon: <CastConnectedIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.devices',
    path: '/devices',
    icon: <DevicesIcon />,
    roles: ['master_full', 'tenant_admin'],
  },
  {
    labelKey: 'nav.health',
    path: '/health',
    icon: <HealthAndSafetyIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.dealers',
    path: '/dealers',
    icon: <StoreIcon />,
    roles: ['master_full'],
  },
  {
    labelKey: 'nav.applications',
    path: '/applications',
    icon: <AppsIcon />,
    roles: ['master_full', 'dealer_admin'],
  },
  {
    labelKey: 'nav.tenants',
    path: '/tenants',
    icon: <GroupsIcon />,
    roles: ['master_full', 'dealer_admin'],
  },
  {
    labelKey: 'nav.users',
    path: '/users',
    icon: <PeopleIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.tickets',
    path: '/tickets',
    icon: <SupportAgentIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.faq',
    path: '/faq',
    icon: <HelpOutlineIcon />,
    roles: ['master_full', 'dealer_admin', 'tenant_admin'],
  },
  {
    labelKey: 'nav.audit',
    path: '/audit-logs',
    icon: <HistoryIcon />,
    roles: ['master_full'],
  },
];

const ROUTE_META: Record<string, { titleKey: string; subtitleKey: string }> = {
  '/metrics': { titleKey: 'nav.dashboard', subtitleKey: 'pageSubtitle.dashboard' },
  '/sessions': { titleKey: 'nav.sessions', subtitleKey: 'pageSubtitle.sessions' },
  '/devices': { titleKey: 'nav.devices', subtitleKey: 'pageSubtitle.devices' },
  '/dealers': { titleKey: 'nav.dealers', subtitleKey: 'pageSubtitle.dealers' },
  '/applications': { titleKey: 'nav.applications', subtitleKey: 'pageSubtitle.applications' },
  '/tenants': { titleKey: 'nav.tenants', subtitleKey: 'pageSubtitle.tenants' },
  '/users': { titleKey: 'nav.users', subtitleKey: 'pageSubtitle.users' },
  '/tickets': { titleKey: 'nav.tickets', subtitleKey: 'pageSubtitle.tickets' },
  '/faq': { titleKey: 'nav.faq', subtitleKey: 'pageSubtitle.faq' },
  '/audit-logs': { titleKey: 'nav.audit', subtitleKey: 'pageSubtitle.audit' },
  '/profile': { titleKey: 'nav.profile', subtitleKey: 'pageSubtitle.profile' },
};

function getPageMeta(pathname: string) {
  for (const [prefix, meta] of Object.entries(ROUTE_META)) {
    if (pathname.startsWith(prefix)) {
      return meta;
    }
  }
  return { titleKey: 'app.title', subtitleKey: '' };
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const { i18n } = useTranslation();

  const languages = [
    { code: 'pt-BR', label: 'Português (BR)' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangAnchor(null);
  };

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const filteredNavItems = NAV_ITEMS.filter((item) => {
    if (!item.roles || item.roles.length === 0) {
      return true;
    }
    if (!user) {
      return false;
    }
    return item.roles.includes(user.role);
  });

  const isActive = (itemPath: string) => {
    return location.pathname.startsWith(itemPath);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    setDrawerOpen(false);
    setIsLogoutConfirmOpen(true);
  };

  const confirmLogout = () => {
    logout();
    setIsLogoutConfirmOpen(false);
  };

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const pageMeta = getPageMeta(location.pathname);
  const pageTitle = t(pageMeta.titleKey);
  const pageSubtitle = pageMeta.subtitleKey ? t(pageMeta.subtitleKey) : '';

  const sidebarContent = (isTemporary: boolean = false) => (
    <Box
      sx={{
        width: isTemporary ? 280 : currentSidebarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        overflow: 'hidden',
      }}
    >
      {/* Logo / Brand */}
      <Box
        sx={{
          height: HEADER_HEIGHT,
          px: sidebarCollapsed && !isTemporary ? 0 : 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed && !isTemporary ? 'center' : 'flex-start',
          gap: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            width: 36,
            height: 36,
          }}
        >
          <ShieldIcon fontSize="small" />
        </Avatar>
        {(!sidebarCollapsed || isTemporary) && (
          <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
            {t('app.title')}
          </Typography>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, py: 1.5, px: 1 }}>
        {filteredNavItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip
              title={sidebarCollapsed && !isTemporary ? t(item.labelKey) : ''}
              placement="right"
            >
              <ListItemButton
                onClick={() => handleNavClick(item.path)}
                selected={isActive(item.path)}
                sx={{
                  minHeight: 44,
                  justifyContent: sidebarCollapsed && !isTemporary ? 'center' : 'initial',
                  px: sidebarCollapsed && !isTemporary ? 1.5 : 2,
                  borderRadius: 1,
                  border: 'none',
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: sidebarCollapsed && !isTemporary ? 0 : 1.5,
                    justifyContent: 'center',
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(!sidebarCollapsed || isTemporary) && (
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive(item.path) ? 600 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: sidebarCollapsed && !isTemporary ? 1 : 2 }} />

      {/* Bottom: Collapse + Logout */}
      <List sx={{ px: 1, py: 1.5 }}>
        {/* Collapse Toggle */}
        {!isTemporary && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={sidebarCollapsed ? t('sidebar.expand') : ''} placement="right">
              <ListItemButton
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{
                  minHeight: 44,
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  px: sidebarCollapsed ? 1.5 : 2,
                  borderRadius: 1,
                  border: 'none',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&:active': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: sidebarCollapsed ? 0 : 1.5,
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={t(sidebarCollapsed ? 'sidebar.expand' : 'sidebar.collapse')}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 400,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}

        {/* Logout */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <Tooltip
            title={sidebarCollapsed && !isTemporary ? t('app.logout') : ''}
            placement="right"
          >
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 44,
                justifyContent: sidebarCollapsed && !isTemporary ? 'center' : 'initial',
                px: sidebarCollapsed && !isTemporary ? 1.5 : 2,
                borderRadius: 1,
                border: 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '&:active': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: sidebarCollapsed && !isTemporary ? 0 : 1.5,
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {(!sidebarCollapsed || isTemporary) && (
                <ListItemText
                  primary={t('app.logout')}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 400,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Permanent Sidebar for Desktop */}
      {!isMobile && (
        <Box sx={{ position: 'relative' }}>
          <Drawer
            variant="permanent"
            sx={{
              width: currentSidebarWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: currentSidebarWidth,
                boxSizing: 'border-box',
                borderRight: 1,
                borderColor: 'divider',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflow: 'visible',
              },
            }}
          >
            {sidebarContent(false)}
          </Drawer>
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { borderRadius: 0 },
          }}
        >
          {sidebarContent(true)}
        </Drawer>
      )}

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: isMobile ? '100%' : `calc(100% - ${currentSidebarWidth}px)`,
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Header */}
        <Box
          component="header"
          sx={{
            height: HEADER_HEIGHT,
            minHeight: HEADER_HEIGHT,
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Left: hamburger (mobile) + title/subtitle */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
            {isMobile && (
              <Tooltip title={t('nav.toggleMenu')}>
                <span>
                  <CustomButton
                    icon={<MenuIcon fontSize="small" />}
                    iconPosition="only"
                    customSize={{ width: 36, height: 36 }}
                    onClick={() => setDrawerOpen(true)}
                    variant="icon"
                  />
                </span>
              </Tooltip>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                noWrap
                sx={{ lineHeight: 1.2, fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {pageTitle}
              </Typography>
              {pageSubtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block', lineHeight: 1.3 }}
                >
                  {pageSubtitle}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Right: language, theme toggle, profile, notifications */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title={t('language.label')}>
              <span>
                <CustomButton
                  variant="icon"
                  icon={<LanguageIcon fontSize="small" />}
                  iconPosition="only"
                  customSize={{ width: 36, height: 36 }}
                  onClick={(e) => setLangAnchor(e.currentTarget)}
                  aria-label={t('language.label')}
                />
              </span>
            </Tooltip>

            <Tooltip
              title={mode === 'dark' ? t('app.switchToLightMode') : t('app.switchToDarkMode')}
            >
              <span>
                <CustomButton
                  icon={
                    mode === 'dark' ? (
                      <LightModeIcon fontSize="small" />
                    ) : (
                      <DarkModeIcon fontSize="small" />
                    )
                  }
                  iconPosition="only"
                  customSize={{ width: 36, height: 36 }}
                  onClick={toggleTheme}
                  variant="icon"
                  iconColor={theme.palette.text.secondary}
                  aria-label={
                    mode === 'dark' ? t('app.switchToLightMode') : t('app.switchToDarkMode')
                  }
                />
              </span>
            </Tooltip>

            <Tooltip title={t('nav.profile')}>
              <span>
                <CustomButton
                  icon={<PersonIcon fontSize="small" />}
                  iconPosition="only"
                  customSize={{ width: 36, height: 36 }}
                  onClick={() => navigate('/profile')}
                  variant="icon"
                  iconColor={theme.palette.text.secondary}
                  aria-label={t('nav.profile')}
                />
              </span>
            </Tooltip>

            <NotificationBadge />
          </Stack>

          {/* Language menu */}
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={lang.code === i18n.language}
                onClick={() => handleLangChange(lang.code)}
              >
                {lang.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Page content */}
        <Box
          sx={{
            flex: 1,
            py: { xs: 2, md: 3 },
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: { xs: '100%', lg: 1400, xl: 1600 },
            mx: 'auto',
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Logout confirmation modal */}
      <LogoutConfirmationModal
        open={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={confirmLogout}
      />
    </Box>
  );
}
